import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { LayoutDashboard, Users, Building2, FileText, Settings, Save, Loader2, Plus, Pencil, Trash2, Eye, TrendingUp, BadgeCheck, CheckCircle, XCircle, Clock, BarChart3, Sparkles, Phone, Mail, ChevronDown, Menu, Image, X, Star, MessageCircle, Send, Bell, Play, RefreshCw, Wrench, Database, AlertTriangle, Crown, Search, Code, ChevronUp, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { AdminAnalyticsDashboard } from '../components/AnalyticsDashboard';
import { RichTextEditor } from '../components/RichTextEditor';
import AdminMaintenanceTools from '../components/admin/AdminMaintenanceTools';
import AdminOnboarding from '../components/admin/AdminOnboarding';
import AdminWhatsApp from '../components/admin/AdminWhatsApp';
import AdminEmailCampaign from '../components/admin/AdminEmailCampaign';
import AdminWeeklyReportCenters from '../components/admin/AdminWeeklyReportCenters';
import AdminRehabAppBroadcast from '../components/admin/AdminRehabAppBroadcast';
import AdminReto21Broadcast from '../components/AdminReto21Broadcast';
import AdminVamosYa from '../components/AdminVamosYa';
import AdminCenters from '../components/admin/AdminCenters';
import AdminWorkshops from '../components/admin/AdminWorkshops';
import AdminAsesoria from '../components/admin/AdminAsesoria';
import AdminProfessionals from '../components/admin/AdminProfessionals';
import AdminSubscriptions from '../components/admin/AdminSubscriptions';
import AdminLeadsNewsletter from '../components/admin/AdminLeadsNewsletter';
import AdminQuoteRequests from '../components/admin/AdminQuoteRequests';
import AdminTestimonios from '../components/admin/AdminTestimonios';
import AdminRecentActivity from '../components/admin/AdminRecentActivity';
import EngagementDashboard from './admin/EngagementDashboard';
import { SEOAnalyzer } from '../components/blog/SEOAnalyzer';
import { BLOG_CATEGORIES, getAuthorByCategory } from '../config/authors';
import { TemplateSelector } from '../components/blog/ArticleTemplates';
import authAxios, { API } from '../utils/authAxios';

var cities = ['Santiago', 'Valparaíso', 'Concepción', 'La Serena', 'Antofagasta', 'Temuco', 'Rancagua', 'Talca', 'Arica', 'Puerto Montt'];
var serviceOptions = ['Desintoxicación', 'Internación', 'Ambulatorio', 'Terapia grupal', 'Terapia individual', 'Terapia familiar', 'Programa 12 pasos'];
var specializationOptions = ['Alcoholismo', 'Drogadicción', 'Adicción a medicamentos', 'Ludopatía', 'Adicciones conductuales', 'Cocaína', 'Marihuana', 'Pasta base'];
var modalityOptions = [
  { value: 'residencial', label: 'Residencial' },
  { value: 'ambulatorio', label: 'Ambulatorio' },
  { value: 'online', label: 'Online' },
  { value: 'mixto', label: 'Modalidad Mixta' }
];
var genderOptions = [
  { value: 'hombre', label: 'Solo Hombres' },
  { value: 'mujer', label: 'Solo Mujeres' },
  { value: 'mixto', label: 'Mixto' }
];
var workshopOptions = ['Teatro', 'Meditación', 'Habilidades sociales', 'Prevención de recaída', 'Deportes', 'Arte', 'Música', 'Yoga'];
var professionalTypes = [
  { value: 'therapist', label: 'Terapeuta en Adicciones' },
  { value: 'psychologist', label: 'Psicólogo' },
  { value: 'psychiatrist', label: 'Psiquiatra' },
  { value: 'counselor', label: 'Consejero' }
];

export function AdminDashboard() {
  var [stats, setStats] = useState(null);
  var [recentStats, setRecentStats] = useState(null);
  var [loading, setLoading] = useState(true);
  var [activeTab, setActiveTab] = useState('overview');

  // Deep-link via ?tab=...
  useEffect(function() {
    try {
      var params = new URLSearchParams(window.location.search);
      var t = params.get('tab');
      if (t === 'reto21' || t === 'engagement') setActiveTab('engagement');
      else if (t) setActiveTab(t);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(function() {
    async function load() {
      try {
        var res = await authAxios.get(API + '/admin/stats', { withCredentials: true });
        setStats(res.data);
        
        // Load recent activity summary
        try {
          var recentRes = await authAxios.get(API + '/admin/recent-activity?period=all&limit=100', { withCredentials: true });
          setRecentStats(recentRes.data.summary);
        } catch (e) {
          console.log('Recent activity not available');
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <div className="flex justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
      </div>
    );
  }

  var statCards = [
    { label: 'Usuarios totales', value: stats?.total_users || 0, icon: Users },
    { label: 'Pacientes', value: stats?.total_patients || 0, icon: Users },
    { label: 'Profesionales', value: stats?.total_professionals || 0, icon: Users },
    { label: 'Profesionales públicos', value: stats?.public_professionals || 0, icon: Eye },
    { label: 'Centros totales', value: stats?.total_centers || 0, icon: Building2 },
    { label: 'Centros públicos', value: stats?.public_centers || 0, icon: Eye },
    { label: 'Artículos totales', value: stats?.total_articles || 0, icon: FileText },
    { label: 'Artículos publicados', value: stats?.published_articles || 0, icon: Eye }
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-50 mb-2" data-testid="admin-dashboard-title">Panel de Administración</h1>
            <p className="text-slate-400">Gestiona usuarios, centros, profesionales y contenido</p>
          </div>

          {/* Menu compacto con dropdown */}
          <div className="flex items-center gap-3 mb-6">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800 min-w-[200px] justify-between">
                  <span className="flex items-center gap-2">
                    <Menu className="w-4 h-4" />
                    {activeTab === 'overview' && 'Resumen'}
                    {activeTab === 'recent-activity' && 'Actividad Reciente'}
                    {activeTab === 'analytics' && 'Analíticas'}
                    {activeTab === 'engagement' && 'Engagement (Experiencias + Reto 21)'}
                    {activeTab === 'asesoria' && 'Asesorías'}
                    {activeTab === 'users' && 'Usuarios'}
                    {activeTab === 'centers' && 'Centros'}
                    {activeTab === 'professionals' && 'Profesionales'}
                    {activeTab === 'appointments' && 'Citas'}
                    {activeTab === 'verification' && 'Verificaciones'}
                    {activeTab === 'articles' && 'Blog'}
                    {activeTab === 'content' && 'Contenido'}
                    {activeTab === 'featured' && 'Destacados'}
                    {activeTab === 'leads' && 'Leads Chat'}
                    {activeTab === 'center-contacts' && 'Contactos Centros'}
                    {activeTab === 'commissions' && 'Comisiones'}
                    {activeTab === 'workshops' && 'Talleres'}
                    {activeTab === 'whatsapp' && 'WhatsApp'}
                    {activeTab === 'email-campaign' && 'Email Marketing'}
                    {activeTab === 'weekly-centers' && '📊 Reporte semanal a centros'}
                    {activeTab === 'reto21-broadcast' && '📊 Broadcast Estudio Reto 21'}
                    {activeTab === 'vamosya' && '📊 ¡Vamos Ya! · Dashboard de Efectividad'}
                    {activeTab === 'onboarding' && 'Onboarding'}
                    {activeTab === 'tools' && 'Herramientas'}
                    {activeTab === 'subscriptions' && 'Suscripciones'}
                    {activeTab === 'leads-newsletter' && 'Leads y Newsletter'}
                    {activeTab === 'quote-requests' && 'Cotizaciones'}
                    {activeTab === 'testimonios' && 'Testimonios & Encuestas'}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-slate-900 border-slate-700 w-56">
                <DropdownMenuItem onClick={function() { setActiveTab('overview'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'overview' ? 'bg-emerald-500/20' : '')}>
                  <LayoutDashboard className="w-4 h-4 mr-2" />Resumen
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('recent-activity'); }} className={'text-green-400 focus:bg-green-500/20 cursor-pointer ' + (activeTab === 'recent-activity' ? 'bg-green-500/20' : '')}>
                  <Activity className="w-4 h-4 mr-2" />Actividad Reciente {recentStats?.new_count > 0 && <span className="ml-auto bg-green-500 text-white text-xs px-1.5 rounded-full">{recentStats.new_count}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('analytics'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'analytics' ? 'bg-emerald-500/20' : '')}>
                  <BarChart3 className="w-4 h-4 mr-2" />Analíticas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('engagement'); }} className={'text-emerald-400 focus:bg-emerald-500/20 cursor-pointer ' + (activeTab === 'engagement' ? 'bg-emerald-500/20' : '')}>
                  <Sparkles className="w-4 h-4 mr-2" />Engagement (Experiencias + Reto 21)
                </DropdownMenuItem>
                <Link to="/admin/reto21-insights" data-testid="admin-reto21-insights-link">
                  <DropdownMenuItem className="text-cyan-400 focus:bg-cyan-500/20 cursor-pointer">
                    <BarChart3 className="w-4 h-4 mr-2" />Reto 21 Insights
                  </DropdownMenuItem>
                </Link>
                <Link to="/admin/reto21-testimonios" data-testid="admin-reto21-testimonios-link">
                  <DropdownMenuItem className="text-amber-400 focus:bg-amber-500/20 cursor-pointer">
                    <Sparkles className="w-4 h-4 mr-2" />Reto 21 Testimonios
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('asesoria'); }} className={'text-amber-400 focus:bg-amber-500/20 cursor-pointer ' + (activeTab === 'asesoria' ? 'bg-amber-500/20' : '')}>
                  <Sparkles className="w-4 h-4 mr-2" />Asesorías
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('users'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'users' ? 'bg-emerald-500/20' : '')}>
                  <Users className="w-4 h-4 mr-2" />Usuarios
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('centers'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'centers' ? 'bg-emerald-500/20' : '')}>
                  <Building2 className="w-4 h-4 mr-2" />Centros
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('professionals'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'professionals' ? 'bg-emerald-500/20' : '')}>
                  <Users className="w-4 h-4 mr-2" />Profesionales
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('appointments'); }} className={'text-blue-400 focus:bg-blue-500/20 cursor-pointer ' + (activeTab === 'appointments' ? 'bg-blue-500/20' : '')}>
                  <Clock className="w-4 h-4 mr-2" />Citas
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('verification'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'verification' ? 'bg-emerald-500/20' : '')}>
                  <BadgeCheck className="w-4 h-4 mr-2" />Verificaciones
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('articles'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'articles' ? 'bg-emerald-500/20' : '')}>
                  <FileText className="w-4 h-4 mr-2" />Blog
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('content'); }} className={'text-slate-200 focus:bg-slate-800 cursor-pointer ' + (activeTab === 'content' ? 'bg-emerald-500/20' : '')}>
                  <Settings className="w-4 h-4 mr-2" />Contenido
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('featured'); }} className={'text-amber-400 focus:bg-amber-500/20 cursor-pointer ' + (activeTab === 'featured' ? 'bg-amber-500/20' : '')}>
                  <Sparkles className="w-4 h-4 mr-2" />Centros Destacados
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('leads'); }} className={'text-purple-400 focus:bg-purple-500/20 cursor-pointer ' + (activeTab === 'leads' ? 'bg-purple-500/20' : '')}>
                  <Mail className="w-4 h-4 mr-2" />Leads TerapeutAI
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('center-contacts'); }} className={'text-blue-400 focus:bg-blue-500/20 cursor-pointer ' + (activeTab === 'center-contacts' ? 'bg-blue-500/20' : '')}>
                  <Users className="w-4 h-4 mr-2" />Contactos Centros
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('commissions'); }} className={'text-amber-400 focus:bg-amber-500/20 cursor-pointer ' + (activeTab === 'commissions' ? 'bg-amber-500/20' : '')}>
                  <Sparkles className="w-4 h-4 mr-2" />Comisiones
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('workshops'); }} className={'text-purple-400 focus:bg-purple-500/20 cursor-pointer ' + (activeTab === 'workshops' ? 'bg-purple-500/20' : '')}>
                  <Sparkles className="w-4 h-4 mr-2" />Talleres
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('whatsapp'); }} className={'text-green-400 focus:bg-green-500/20 cursor-pointer ' + (activeTab === 'whatsapp' ? 'bg-green-500/20' : '')}>
                  <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('vamosya'); }} className={'text-emerald-300 focus:bg-emerald-500/20 cursor-pointer ' + (activeTab === 'vamosya' ? 'bg-emerald-500/20' : '')} data-testid="admin-tab-vamosya">
                  <BarChart3 className="w-4 h-4 mr-2" />¡Vamos Ya! · Efectividad
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('email-campaign'); }} className={'text-blue-400 focus:bg-blue-500/20 cursor-pointer ' + (activeTab === 'email-campaign' ? 'bg-blue-500/20' : '')}>
                  <Mail className="w-4 h-4 mr-2" />Email Marketing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('weekly-centers'); }} className={'text-amber-400 focus:bg-amber-500/20 cursor-pointer ' + (activeTab === 'weekly-centers' ? 'bg-amber-500/20' : '')} data-testid="admin-tab-weekly-centers">
                  <Mail className="w-4 h-4 mr-2" />📊 Reporte semanal centros
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('reto21-broadcast'); }} className={'text-emerald-300 focus:bg-emerald-500/20 cursor-pointer ' + (activeTab === 'reto21-broadcast' ? 'bg-emerald-500/20' : '')} data-testid="admin-tab-reto21-broadcast">
                  <Mail className="w-4 h-4 mr-2" />Broadcast Estudio Reto 21
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('rehabapp-broadcast'); }} className={'text-teal-300 focus:bg-teal-500/20 cursor-pointer ' + (activeTab === 'rehabapp-broadcast' ? 'bg-teal-500/20' : '')} data-testid="admin-tab-rehabapp-broadcast">
                  <Mail className="w-4 h-4 mr-2" />Broadcast RehabApp
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('onboarding'); }} className={'text-cyan-400 focus:bg-cyan-500/20 cursor-pointer ' + (activeTab === 'onboarding' ? 'bg-cyan-500/20' : '')}>
                  <RefreshCw className="w-4 h-4 mr-2" />Secuencias Onboarding
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem onClick={function() { setActiveTab('tools'); }} className={'text-orange-400 focus:bg-orange-500/20 cursor-pointer ' + (activeTab === 'tools' ? 'bg-orange-500/20' : '')}>
                  <Wrench className="w-4 h-4 mr-2" />Herramientas
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('subscriptions'); }} className={'text-amber-400 focus:bg-amber-500/20 cursor-pointer ' + (activeTab === 'subscriptions' ? 'bg-amber-500/20' : '')}>
                  <Crown className="w-4 h-4 mr-2" />Suscripciones
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('leads-newsletter'); }} className={'text-emerald-400 focus:bg-emerald-500/20 cursor-pointer ' + (activeTab === 'leads-newsletter' ? 'bg-emerald-500/20' : '')}>
                  <TrendingUp className="w-4 h-4 mr-2" />Leads y Newsletter
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('quote-requests'); }} className={'text-blue-400 focus:bg-blue-500/20 cursor-pointer ' + (activeTab === 'quote-requests' ? 'bg-blue-500/20' : '')}>
                  <MessageCircle className="w-4 h-4 mr-2" />Cotizaciones
                </DropdownMenuItem>
                <DropdownMenuItem onClick={function() { setActiveTab('testimonios'); }} className={'text-teal-400 focus:bg-teal-500/20 cursor-pointer ' + (activeTab === 'testimonios' ? 'bg-teal-500/20' : '')} data-testid="menu-testimonios">
                  <Star className="w-4 h-4 mr-2" />Testimonios & Encuestas
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Quick access buttons for most used sections */}
            <div className="hidden md:flex gap-2">
              <Button 
                size="sm"
                variant={activeTab === 'appointments' ? 'default' : 'outline'} 
                onClick={function() { setActiveTab('appointments'); }} 
                className={activeTab === 'appointments' ? 'bg-blue-500 hover:bg-blue-600' : 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10'}
              >
                <Clock className="w-4 h-4 mr-1" />Citas
              </Button>
              <Button 
                size="sm"
                variant={activeTab === 'asesoria' ? 'default' : 'outline'} 
                onClick={function() { setActiveTab('asesoria'); }} 
                className={activeTab === 'asesoria' ? 'bg-amber-500 hover:bg-amber-600' : 'border-amber-500/50 text-amber-400 hover:bg-amber-500/10'}
              >
                <Sparkles className="w-4 h-4 mr-1" />Asesorías
              </Button>
              <Button 
                size="sm"
                variant={activeTab === 'centers' ? 'default' : 'outline'} 
                onClick={function() { setActiveTab('centers'); }} 
                className={activeTab === 'centers' ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
              >
                <Building2 className="w-4 h-4 mr-1" />Centros
              </Button>
              <Button 
                size="sm"
                variant={activeTab === 'verification' ? 'default' : 'outline'} 
                onClick={function() { setActiveTab('verification'); }} 
                className={activeTab === 'verification' ? 'bg-emerald-500 hover:bg-emerald-600' : 'border-slate-700 text-slate-300 hover:bg-slate-800'}
              >
                <BadgeCheck className="w-4 h-4 mr-1" />Verificaciones
              </Button>
            </div>
          </div>

          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {statCards.map(function(stat, index) {
                  return (
                    <Card key={index} className="bg-slate-900 border-slate-800">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-2">
                          <stat.icon className="w-5 h-5 text-emerald-400" />
                          <TrendingUp className="w-4 h-4 text-slate-500" />
                        </div>
                        <p className="text-2xl font-bold text-slate-50">{stat.value}</p>
                        <p className="text-sm text-slate-400">{stat.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Recent Activity Summary */}
              {recentStats && (
                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader className="border-b border-slate-800">
                    <CardTitle className="text-slate-50 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-green-400" />
                      Resumen de Entidades Registradas
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={function() { setActiveTab('recent-activity'); }}
                        className="ml-auto text-emerald-400 hover:text-emerald-300"
                      >
                        Ver todo
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-3xl font-bold text-green-400">{recentStats.new_count || 0}</p>
                        <p className="text-sm text-slate-400">Nuevos (48h)</p>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-3xl font-bold text-amber-400">{recentStats.unverified_count || 0}</p>
                        <p className="text-sm text-slate-400">Sin verificar</p>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-3xl font-bold text-red-400">{recentStats.incomplete_count || 0}</p>
                        <p className="text-sm text-slate-400">Incompletos</p>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <p className="text-3xl font-bold text-blue-400">{recentStats.total || 0}</p>
                        <p className="text-sm text-slate-400">Total actividad</p>
                      </div>
                    </div>
                    {recentStats.by_type && (
                      <div className="flex gap-4 mt-4 justify-center flex-wrap">
                        <span className="flex items-center gap-2 text-sm text-emerald-400">
                          <Building2 className="w-4 h-4" /> {recentStats.by_type.centers || 0} Centros
                        </span>
                        <span className="flex items-center gap-2 text-sm text-cyan-400">
                          <Users className="w-4 h-4" /> {recentStats.by_type.professionals || 0} Profesionales
                        </span>
                        <span className="flex items-center gap-2 text-sm text-purple-400">
                          <Sparkles className="w-4 h-4" /> {recentStats.by_type.workshops || 0} Talleres
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Exportar reportes operacionales */}
              <Card className="bg-slate-900 border-teal-800/50" data-testid="admin-exports-card">
                <CardHeader className="border-b border-slate-800">
                  <CardTitle className="text-slate-50 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-teal-400" />
                    Exportar reportes operacionales
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-3">
                  <p className="text-sm text-slate-400">
                    Descargas Excel con datos internos de gerencia. Requieren sesión admin.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={async () => {
                        try {
                          const { data } = await authAxios.get(`${API}/admin/centers/export-sales-report?days=30`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([data]));
                          const a = document.createElement('a');
                          a.href = url;
                          const ts = new Date().toISOString().slice(0,10).replace(/-/g,'');
                          a.download = `centros_ventas_30d_${ts}.xlsx`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Error descargando: ' + (e?.response?.data?.detail || e.message));
                        }
                      }}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="export-centers-sales-report-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Reporte de VENTAS · últimos 30d (.xlsx)
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const { data } = await authAxios.get(`${API}/admin/centers/export-full-report`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([data]));
                          const a = document.createElement('a');
                          a.href = url;
                          const ts = new Date().toISOString().slice(0,10).replace(/-/g,'');
                          a.download = `centros_reporte_operacional_${ts}.xlsx`;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch (e) {
                          alert('Error descargando: ' + (e?.response?.data?.detail || e.message));
                        }
                      }}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="export-centers-full-report-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Reporte operacional de centros (.xlsx)
                    </Button>
                    <a
                      href={`${API}/admin/centers/export-excel`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm"
                      data-testid="export-centers-directory-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Directorio público de centros
                    </a>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">
                    <strong className="text-amber-400">Reporte de VENTAS (30d)</strong>: por centro incluye contacto, plan actual,
                    visitas ficha, clicks WhatsApp/Tel/Maps, cotizaciones desglosadas por fuente
                    (cotizador, widget flotante, ficha del centro, sitio satélite),
                    última cotización recibida, oportunidad de upsell y competidores pagados en su región.
                    Ordenado por potencial de venta.
                    <br /><br />
                    <strong className="text-slate-400">Reporte operacional</strong> incluye:
                    email · teléfono · dirección · plan · plan activo desde · días con plan ·
                    visitas a la ficha · clicks WhatsApp/Teléfono/Formulario · cotizaciones recibidas · verificación · fecha creación.
                    <br />Incluye hoja &quot;Resumen&quot; con MRR estimado y conteo por plan.
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'recent-activity' && <AdminRecentActivity />}

          {activeTab === 'analytics' && <AdminAnalytics />}
          {activeTab === 'engagement' && <EngagementDashboard />}
          {activeTab === 'asesoria' && <AdminAsesoria />}
          {activeTab === 'appointments' && <AdminAppointments />}
          {activeTab === 'users' && <AdminUsers />}
          {activeTab === 'centers' && <AdminCenters />}
          {activeTab === 'professionals' && <AdminProfessionals />}
          {activeTab === 'verification' && <AdminVerification />}
          {activeTab === 'articles' && <AdminArticles />}
          {activeTab === 'content' && <AdminContent />}
          {activeTab === 'featured' && <AdminFeaturedCenters />}
          {activeTab === 'leads' && <AdminChatLeads />}
          {activeTab === 'center-contacts' && <AdminCenterContacts />}
          {activeTab === 'commissions' && <AdminCommissions />}
          {activeTab === 'workshops' && <AdminWorkshops />}
          {activeTab === 'whatsapp' && <AdminWhatsApp />}
          {activeTab === 'email-campaign' && <AdminEmailCampaign />}
          {activeTab === 'weekly-centers' && <AdminWeeklyReportCenters />}
          {activeTab === 'reto21-broadcast' && <AdminReto21Broadcast />}
          {activeTab === 'rehabapp-broadcast' && <AdminRehabAppBroadcast />}
          {activeTab === 'vamosya' && <AdminVamosYa />}
          {activeTab === 'onboarding' && <AdminOnboarding />}
          {activeTab === 'tools' && <AdminMaintenanceTools />}
          {activeTab === 'subscriptions' && <AdminSubscriptions />}
          {activeTab === 'leads-newsletter' && <AdminLeadsNewsletter />}
          {activeTab === 'quote-requests' && <AdminQuoteRequests />}
          {activeTab === 'testimonios' && <AdminTestimonios />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function AdminAnalytics() {
  var [overview, setOverview] = useState(null);
  var [topProfiles, setTopProfiles] = useState(null);
  var [trends, setTrends] = useState(null);
  var [detailedStats, setDetailedStats] = useState(null);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    loadAnalytics();
  }, []);

  async function loadAnalytics() {
    setLoading(true);
    try {
      var [overviewRes, topRes, trendsRes] = await Promise.all([
        authAxios.get(API + '/analytics/admin/overview', { withCredentials: true }),
        authAxios.get(API + '/analytics/admin/top-profiles', { withCredentials: true }),
        authAxios.get(API + '/analytics/admin/trends?days=30', { withCredentials: true })
      ]);
      setOverview(overviewRes.data);
      setTopProfiles(topRes.data);
      setTrends(trendsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar analíticas');
    }
    setLoading(false);
  }

  async function loadDetailedStats() {
    if (detailedStats) return; // Already loaded
    try {
      var res = await authAxios.get(API + '/analytics/admin/detailed?days=30', { withCredentials: true });
      setDetailedStats(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar estadísticas detalladas');
    }
  }

  function exportCSV() {
    window.open(API + '/analytics/admin/export?days=30', '_blank');
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <AdminAnalyticsDashboard 
      overview={overview} 
      topProfiles={topProfiles} 
      trends={trends}
      detailedStats={detailedStats}
      onExport={exportCSV}
      onLoadDetailed={loadDetailedStats}
    />
  );
}

function AdminAppointments() {
  var [appointments, setAppointments] = useState([]);
  var [stats, setStats] = useState({ total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('all');

  useEffect(function() {
    loadAppointments();
  }, [filter]);

  async function loadAppointments() {
    setLoading(true);
    try {
      var url = API + '/admin/appointments?limit=100';
      if (filter !== 'all') url += '&status=' + filter;
      var res = await authAxios.get(url, { withCredentials: true });
      setAppointments(res.data.appointments || []);
      setStats(res.data.stats || { total: 0, pending: 0, confirmed: 0, completed: 0, cancelled: 0 });
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar citas');
    }
    setLoading(false);
  }

  async function updateStatus(appointmentId, newStatus) {
    try {
      await authAxios.put(API + '/appointments/' + appointmentId, { status: newStatus }, { withCredentials: true });
      toast.success('Cita actualizada');
      loadAppointments();
    } catch (e) {
      toast.error('Error al actualizar');
    }
  }

  async function deleteAppointment(appointmentId) {
    if (!window.confirm('¿Eliminar esta cita?')) return;
    try {
      await authAxios.delete(API + '/appointments/' + appointmentId, { withCredentials: true });
      toast.success('Cita eliminada');
      loadAppointments();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  }

  function getStatusBadge(status) {
    var styles = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      confirmed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      completed: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
      no_show: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    var labels = { pending: 'Pendiente', confirmed: 'Confirmada', completed: 'Completada', cancelled: 'Cancelada', no_show: 'No asistió' };
    return <span className={'px-2 py-1 text-xs font-medium rounded-full border ' + (styles[status] || styles.pending)}>{labels[status] || status}</span>;
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center"><Clock className="w-5 h-5 text-slate-400" /></div><div><p className="text-2xl font-bold text-slate-50">{stats.total}</p><p className="text-xs text-slate-500">Total</p></div></div></CardContent>
        </Card>
        <Card className="bg-slate-900 border-yellow-500/30">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center"><Clock className="w-5 h-5 text-yellow-400" /></div><div><p className="text-2xl font-bold text-yellow-400">{stats.pending}</p><p className="text-xs text-slate-500">Pendientes</p></div></div></CardContent>
        </Card>
        <Card className="bg-slate-900 border-blue-500/30">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-blue-400" /></div><div><p className="text-2xl font-bold text-blue-400">{stats.confirmed}</p><p className="text-xs text-slate-500">Confirmadas</p></div></div></CardContent>
        </Card>
        <Card className="bg-slate-900 border-emerald-500/30">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center"><CheckCircle className="w-5 h-5 text-emerald-400" /></div><div><p className="text-2xl font-bold text-emerald-400">{stats.completed}</p><p className="text-xs text-slate-500">Completadas</p></div></div></CardContent>
        </Card>
        <Card className="bg-slate-900 border-red-500/30">
          <CardContent className="p-4"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-400" /></div><div><p className="text-2xl font-bold text-red-400">{stats.cancelled}</p><p className="text-xs text-slate-500">Canceladas</p></div></div></CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Label className="text-slate-400">Filtrar:</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 bg-slate-800 border-slate-700 text-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="all" className="text-slate-200">Todas</SelectItem>
            <SelectItem value="pending" className="text-yellow-400">Pendientes</SelectItem>
            <SelectItem value="confirmed" className="text-blue-400">Confirmadas</SelectItem>
            <SelectItem value="completed" className="text-emerald-400">Completadas</SelectItem>
            <SelectItem value="cancelled" className="text-red-400">Canceladas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Appointments Table */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader><CardTitle className="text-slate-50 flex items-center gap-2"><Clock className="w-5 h-5 text-blue-400" />Todas las Citas</CardTitle></CardHeader>
        <CardContent>
          {appointments.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay citas</p>
          ) : (
            <div className="space-y-4">
              {appointments.map(function(apt) {
                return (
                  <div key={apt.appointment_id} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-semibold text-slate-50">{apt.patient_name}</span>
                          {getStatusBadge(apt.status)}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-400 mb-1">
                          <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{apt.date} {apt.time_slot}</span>
                          <span className="text-emerald-400 font-medium">→ {apt.professional_name}</span>
                          <span>{apt.modality}</span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm">
                          <a href={'mailto:' + apt.patient_email} className="text-slate-500 hover:text-emerald-400">{apt.patient_email}</a>
                          <a href={'https://wa.me/' + apt.patient_phone.replace(/[^0-9]/g, '')} target="_blank" rel="noopener noreferrer" className="text-green-400 hover:text-green-300 flex items-center gap-1"><Phone className="w-3 h-3" />{apt.patient_phone}</a>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Select value={apt.status} onValueChange={function(v) { updateStatus(apt.appointment_id, v); }}>
                          <SelectTrigger className="w-32 bg-slate-800 border-slate-700 text-slate-200 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-slate-800 border-slate-700">
                            <SelectItem value="pending" className="text-yellow-400">Pendiente</SelectItem>
                            <SelectItem value="confirmed" className="text-blue-400">Confirmada</SelectItem>
                            <SelectItem value="completed" className="text-emerald-400">Completada</SelectItem>
                            <SelectItem value="cancelled" className="text-red-400">Cancelada</SelectItem>
                            <SelectItem value="no_show" className="text-slate-400">No asistió</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" className="border-red-500/50 text-red-400 hover:bg-red-500/10" onClick={function() { deleteAppointment(apt.appointment_id); }}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function AdminUsers() {
  var [users, setUsers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [page, setPage] = useState(1);
  var [totalPages, setTotalPages] = useState(1);

  useEffect(function() {
    load();
  }, [page]);

  async function load() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/users?page=' + page + '&limit=20', { withCredentials: true });
      setUsers(res.data.users || []);
      setTotalPages(res.data.pages || 1);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function handleUpdateUser(userId, data) {
    try {
      await authAxios.put(API + '/admin/users/' + userId, data, { withCredentials: true });
      toast.success('Usuario actualizado');
      load();
    } catch (e) {
      toast.error('Error');
    }
  }

  return (
    <Card className="bg-slate-900 border-0 shadow-sm">
      <CardHeader><CardTitle>Gestión de Usuarios</CardTitle></CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4">Usuario</th>
                  <th className="text-left py-3 px-4">Email</th>
                  <th className="text-left py-3 px-4">Rol</th>
                  <th className="text-left py-3 px-4">Verificado</th>
                  <th className="text-left py-3 px-4">Público</th>
                </tr>
              </thead>
              <tbody>
                {users.map(function(u) {
                  return (
                    <tr key={u.user_id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {u.picture ? <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center"><Users className="w-4 h-4 text-emerald-400" /></div>}
                          {u.name}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">{u.email}</td>
                      <td className="py-3 px-4">
                        <Select value={u.role || 'patient'} onValueChange={function(v) { handleUpdateUser(u.user_id, { role: v }); }}>
                          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="patient">Paciente</SelectItem>
                            <SelectItem value="professional">Profesional</SelectItem>
                            <SelectItem value="center">Centro</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="py-3 px-4"><Switch checked={u.is_verified || false} onCheckedChange={function(c) { handleUpdateUser(u.user_id, { is_verified: c }); }} /></td>
                      <td className="py-3 px-4"><Switch checked={u.is_public || false} onCheckedChange={function(c) { handleUpdateUser(u.user_id, { is_public: c }); }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={function() { setPage(function(p) { return Math.max(1, p - 1); }); }} disabled={page === 1}>Anterior</Button>
                <span className="text-sm text-muted-foreground px-4 flex items-center">Página {page} de {totalPages}</span>
                <Button variant="outline" size="sm" onClick={function() { setPage(function(p) { return Math.min(totalPages, p + 1); }); }} disabled={page === totalPages}>Siguiente</Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Professional Edit Form Component
function AdminArticles() {
  var [articles, setArticles] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showDialog, setShowDialog] = useState(false);
  var [editingArticle, setEditingArticle] = useState(null);
  var [formData, setFormData] = useState({ 
    title: '', 
    slug: '',
    content: '', 
    excerpt: '', 
    category: 'general', 
    image: '', 
    tags: '', 
    is_published: false,
    meta_description: '',
    meta_keywords: '',
    reading_time: 5
  });
  var [showHtmlMode, setShowHtmlMode] = useState(false);
  var [htmlContent, setHtmlContent] = useState('');
  var [showTemplateSelector, setShowTemplateSelector] = useState(false);
  var [showSEOAnalyzer, setShowSEOAnalyzer] = useState(true);

  // Auto-generate slug from title
  function generateSlug(title) {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/-+/g, '-') // Replace multiple - with single -
      .trim();
  }

  // Calculate reading time from content
  function calculateReadingTime(content) {
    if (!content) return 5;
    var text = content.replace(/<[^>]*>/g, '');
    var words = text.split(/\s+/).length;
    return Math.max(3, Math.round(words / 200));
  }

  // Apply template to form
  function applyTemplate(template) {
    setFormData(function(prev) {
      return {
        ...prev,
        title: template.structure.title,
        slug: generateSlug(template.structure.title),
        content: template.structure.content,
        excerpt: template.structure.excerpt,
        category: template.suggestedCategory,
        meta_description: template.structure.meta_description,
        meta_keywords: template.structure.meta_keywords
      };
    });
    setHtmlContent(template.structure.content);
    toast.success('Plantilla aplicada. Personaliza el contenido entre corchetes []');
  }

  useEffect(function() {
    load();
  }, []);

  async function load() {
    setLoading(true);
    try {
      var res = await authAxios.get(API + '/admin/articles', { withCredentials: true });
      setArticles(res.data.articles || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  function openNewArticle() {
    setEditingArticle(null);
    setFormData({ 
      title: '', 
      slug: '',
      content: '', 
      excerpt: '', 
      category: 'general', 
      image: '', 
      tags: '', 
      is_published: false,
      meta_description: '',
      meta_keywords: '',
      reading_time: 5
    });
    setShowHtmlMode(false);
    setHtmlContent('');
    setShowDialog(true);
  }

  async function openEditArticle(article) {
    // Load full article content from API
    try {
      var res = await authAxios.get(API + '/admin/articles/' + article.article_id, { withCredentials: true });
      var fullArticle = res.data;
      setEditingArticle(fullArticle);
      setFormData({
        title: fullArticle.title || '',
        slug: fullArticle.slug || '',
        content: fullArticle.content || '',
        excerpt: fullArticle.excerpt || '',
        category: fullArticle.category || 'general',
        image: fullArticle.image || '',
        tags: (fullArticle.tags || []).join(', '),
        is_published: fullArticle.is_published || false,
        meta_description: fullArticle.meta_description || '',
        meta_keywords: fullArticle.meta_keywords || '',
        reading_time: fullArticle.reading_time || 5
      });
      setHtmlContent(fullArticle.content || '');
      setShowHtmlMode(false);
      setShowDialog(true);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar el artículo');
    }
  }

  async function handleSubmit() {
    try {
      // Use HTML content if in HTML mode
      var contentToSave = showHtmlMode ? htmlContent : formData.content;
      
      var data = {
        ...formData,
        content: contentToSave,
        slug: formData.slug || generateSlug(formData.title),
        tags: formData.tags ? formData.tags.split(',').map(function(t) { return t.trim(); }).filter(Boolean) : [],
        reading_time: calculateReadingTime(contentToSave),
        author_name: 'sinadicciones.org'
      };
      
      if (editingArticle) {
        await authAxios.put(API + '/admin/articles/' + editingArticle.article_id, data, { withCredentials: true });
        toast.success('Artículo actualizado');
      } else {
        await authAxios.post(API + '/admin/articles', data, { withCredentials: true });
        toast.success('Artículo creado');
      }
      setShowDialog(false);
      setEditingArticle(null);
      setFormData({ 
        title: '', 
        slug: '',
        content: '', 
        excerpt: '', 
        category: 'general', 
        image: '', 
        tags: '', 
        is_published: false,
        meta_description: '',
        meta_keywords: '',
        reading_time: 5
      });
      setShowHtmlMode(false);
      setHtmlContent('');
      load();
    } catch (e) {
      toast.error('Error al guardar');
    }
  }

  async function handleDelete(articleId) {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    try {
      await authAxios.delete(API + '/admin/articles/' + articleId, { withCredentials: true });
      toast.success('Artículo eliminado');
      load();
    } catch (e) {
      toast.error('Error al eliminar');
    }
  }

  return (
    <Card className="bg-slate-900 border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Gestión de Blog</CardTitle>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={openNewArticle}><Plus className="w-4 h-4 mr-2" />Nuevo artículo</Button>
            </DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <DialogTitle>{editingArticle ? 'Editar artículo' : 'Nuevo artículo'}</DialogTitle>
                  {!editingArticle && (
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      onClick={function() { setShowTemplateSelector(true); }}
                      className="gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Usar Plantilla
                    </Button>
                  )}
                </div>
              </DialogHeader>
              
              {/* Template Selector Modal */}
              <TemplateSelector 
                open={showTemplateSelector} 
                onOpenChange={setShowTemplateSelector}
                onSelectTemplate={applyTemplate}
              />
              
              <div className="space-y-4 py-4">
                {/* Title */}
                <div>
                  <Label>Título *</Label>
                  <Input 
                    value={formData.title} 
                    onChange={function(e) { 
                      var newTitle = e.target.value;
                      setFormData(function(prev) { 
                        return Object.assign({}, prev, { 
                          title: newTitle,
                          slug: prev.slug || generateSlug(newTitle)
                        }); 
                      }); 
                    }} 
                    placeholder="Título del artículo" 
                  />
                </div>
                
                {/* Slug */}
                <div>
                  <Label>URL (slug)</Label>
                  <div className="flex gap-2 items-center">
                    <span className="text-sm text-slate-400">/blog/</span>
                    <Input 
                      value={formData.slug} 
                      onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }); }); }} 
                      placeholder="url-del-articulo"
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Se genera automáticamente desde el título. Puedes editarlo manualmente.</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Categoría</Label>
                    <Select value={formData.category || 'general'} onValueChange={function(v) { setFormData(function(prev) { const auto = getAuthorByCategory(v); return Object.assign({}, prev, { category: v, author_name: auto ? auto.name : prev.author_name }); }); }}>
                      <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
                      <SelectContent>
                        {BLOG_CATEGORIES.map(function(c) {
                          return <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    {formData.category && (() => {
                      const a = getAuthorByCategory(formData.category);
                      if (!a) return null;
                      return (
                        <p className="text-xs text-slate-500 mt-1" data-testid="auto-author-hint">
                          Autor asignado automáticamente: <span className="text-emerald-400 font-medium">{a.name}</span>
                        </p>
                      );
                    })()}
                  </div>
                  <div><Label>Imagen destacada (URL)</Label><Input value={formData.image || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { image: e.target.value }); }); }} placeholder="https://ejemplo.com/imagen.jpg" /></div>
                </div>
                
                {/* Excerpt / Meta Description */}
                <div>
                  <Label>Extracto / Resumen</Label>
                  <Textarea 
                    value={formData.excerpt} 
                    onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { excerpt: e.target.value }); }); }} 
                    rows={2} 
                    placeholder="Breve descripción del artículo (se usa como preview en listados)..." 
                  />
                </div>

                {/* SEO Section */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-4">
                  <div className="flex items-center gap-2 text-emerald-400 font-medium">
                    <Search className="w-4 h-4" />
                    SEO - Optimización para Google
                  </div>
                  
                  {/* Meta Description */}
                  <div>
                    <div className="flex justify-between items-center">
                      <Label>Meta Description</Label>
                      <span className={'text-xs ' + ((formData.meta_description || '').length > 160 ? 'text-red-400' : (formData.meta_description || '').length > 120 ? 'text-emerald-400' : 'text-slate-400')}>
                        {(formData.meta_description || '').length}/160
                      </span>
                    </div>
                    <Textarea 
                      value={formData.meta_description || ''} 
                      onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { meta_description: e.target.value }); }); }} 
                      rows={2} 
                      placeholder="Descripción que aparece en Google (150-160 caracteres ideal)..." 
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Esta descripción aparece en los resultados de búsqueda de Google.</p>
                  </div>
                  
                  {/* Meta Keywords */}
                  <div>
                    <Label>Palabras Clave (Keywords)</Label>
                    <Input 
                      value={formData.meta_keywords || ''} 
                      onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { meta_keywords: e.target.value }); }); }} 
                      placeholder="rehabilitación, adicciones, tratamiento, Chile..."
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-500 mt-1">Palabras clave separadas por coma para SEO.</p>
                  </div>
                </div>
                
                {/* Content Editor with HTML Toggle */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <Label>Contenido *</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-400">Modo:</span>
                      <Button 
                        type="button" 
                        variant={!showHtmlMode ? "default" : "outline"} 
                        size="sm"
                        onClick={function() { 
                          if (showHtmlMode) {
                            setFormData(function(prev) { return Object.assign({}, prev, { content: htmlContent }); });
                          }
                          setShowHtmlMode(false); 
                        }}
                        className="text-xs h-7"
                      >
                        Visual
                      </Button>
                      <Button 
                        type="button" 
                        variant={showHtmlMode ? "default" : "outline"} 
                        size="sm"
                        onClick={function() { 
                          setHtmlContent(formData.content);
                          setShowHtmlMode(true); 
                        }}
                        className="text-xs h-7"
                      >
                        HTML
                      </Button>
                    </div>
                  </div>
                  
                  {showHtmlMode ? (
                    <div className="space-y-2">
                      <Textarea 
                        value={htmlContent}
                        onChange={function(e) { setHtmlContent(e.target.value); }}
                        rows={15}
                        className="font-mono text-sm bg-slate-950"
                        placeholder="<h2>Título</h2><p>Contenido HTML...</p>"
                      />
                      <p className="text-xs text-slate-500">Puedes pegar HTML directamente. Incluye tablas con &lt;table&gt;, botones con &lt;a class='...'&gt;, etc.</p>
                    </div>
                  ) : (
                    <RichTextEditor 
                      content={formData.content} 
                      onChange={function(html) { setFormData(function(prev) { return Object.assign({}, prev, { content: html }); }); }}
                      placeholder="Escribe el contenido del artículo..."
                      onSEOFieldsGenerated={function(seoData) {
                        setFormData(function(prev) {
                          return Object.assign({}, prev, {
                            title: seoData.title || prev.title,
                            slug: seoData.slug || generateSlug(seoData.title) || prev.slug,
                            meta_description: seoData.meta_description || prev.meta_description,
                            meta_keywords: seoData.meta_keywords || prev.meta_keywords,
                            category: seoData.category || prev.category
                          });
                        });
                      }}
                    />
                  )}
                </div>
                
                <div><Label>Tags (separados por coma)</Label><Input value={formData.tags || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { tags: e.target.value }); }); }} placeholder="Tratamiento, Guía, Prevención" /></div>
                
                {/* SEO Analyzer - Real-time Analysis */}
                <div>
                  <button
                    type="button"
                    onClick={function() { setShowSEOAnalyzer(!showSEOAnalyzer); }}
                    className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 transition-colors mb-2"
                  >
                    <BarChart3 className="w-4 h-4" />
                    Análisis SEO en Tiempo Real
                    {showSEOAnalyzer ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {showSEOAnalyzer && (
                    <SEOAnalyzer
                      title={formData.title}
                      content={showHtmlMode ? htmlContent : formData.content}
                      metaDescription={formData.meta_description}
                      metaKeywords={formData.meta_keywords}
                      excerpt={formData.excerpt}
                    />
                  )}
                </div>
                
                <div className="flex items-center gap-2"><Switch checked={formData.is_published} onCheckedChange={function(c) { setFormData(function(prev) { return Object.assign({}, prev, { is_published: c }); }); }} /><Label>Publicar inmediatamente</Label></div>
                <Button onClick={handleSubmit} className="w-full bg-emerald-500 hover:bg-emerald-600"><Save className="w-4 h-4 mr-2" />{editingArticle ? 'Actualizar' : 'Guardar'}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12"><FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" /><p className="text-muted-foreground">No hay artículos</p></div>
        ) : (
          <div className="space-y-4">
            {articles.map(function(a) {
              return (
                <div key={a.article_id} className="flex items-center justify-between p-4 bg-slate-800 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{a.title}</h4>
                      {a.is_published ? <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Publicado</span> : <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Borrador</span>}
                      {a.category && <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded-full">{a.category}</span>}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{a.excerpt}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={function() { openEditArticle(a); }}><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" asChild><Link to={'/blog/' + a.slug} target="_blank"><Eye className="w-4 h-4" /></Link></Button>
                    <Button variant="ghost" size="sm" onClick={function() { handleDelete(a.article_id); }}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminVerification() {
  var [requests, setRequests] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('pending');

  useEffect(function() {
    load();
  }, [filter]);

  async function load() {
    setLoading(true);
    try {
      var url = API + '/admin/verification-requests';
      if (filter && filter !== 'all') url += '?status=' + filter;
      var res = await authAxios.get(url, { withCredentials: true });
      setRequests(res.data.requests || []);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function handleAction(verificationId, status, adminNotes) {
    try {
      await authAxios.put(API + '/admin/verification/' + verificationId, {
        status: status,
        admin_notes: adminNotes || null
      }, { withCredentials: true });
      toast.success(status === 'verified' ? 'Centro verificado' : status === 'scheduled' ? 'Verificación programada' : 'Solicitud rechazada');
      load();
    } catch (e) {
      toast.error('Error al procesar solicitud');
    }
  }

  function getStatusBadge(status) {
    if (status === 'pending') return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-400 text-xs rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />Pendiente</span>;
    if (status === 'scheduled') return <span className="px-2 py-1 bg-blue-500/10 text-blue-400 text-xs rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />Programada</span>;
    if (status === 'in_review') return <span className="px-2 py-1 bg-purple-500/10 text-purple-400 text-xs rounded-full flex items-center gap-1"><Eye className="w-3 h-3" />En revisión</span>;
    if (status === 'verified') return <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />Verificado</span>;
    if (status === 'rejected') return <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" />Rechazado</span>;
    return null;
  }

  return (
    <Card className="bg-slate-900 border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <BadgeCheck className="w-5 h-5 text-emerald-400" />
            Solicitudes de Verificación
          </CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-slate-200">Todas</SelectItem>
              <SelectItem value="pending" className="text-slate-200">Pendientes</SelectItem>
              <SelectItem value="scheduled" className="text-slate-200">Programadas</SelectItem>
              <SelectItem value="verified" className="text-slate-200">Verificadas</SelectItem>
              <SelectItem value="rejected" className="text-slate-200">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <BadgeCheck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay solicitudes {filter !== 'all' ? 'en este estado' : ''}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map(function(req) {
              return (
                <div key={req.verification_id} className="p-4 bg-slate-800/50 rounded-xl">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-slate-200">{req.center_name}</span>
                        <span className="px-2 py-0.5 bg-slate-700 text-slate-300 text-xs rounded">
                          {req.verification_type === 'videocall' ? 'Videollamada' : 'Visita presencial'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-400">{new Date(req.created_at).toLocaleDateString('es-CL')}</p>
                    </div>
                    {getStatusBadge(req.status)}
                  </div>
                  
                  {/* Verification commitments */}
                  <div className="flex gap-4 mb-3 text-xs">
                    <span className={req.has_dignified_treatment ? 'text-emerald-400' : 'text-slate-500'}>
                      {req.has_dignified_treatment ? '✓' : '✗'} Tratamiento digno
                    </span>
                    <span className={req.has_therapy_programs ? 'text-emerald-400' : 'text-slate-500'}>
                      {req.has_therapy_programs ? '✓' : '✗'} Programas de terapia
                    </span>
                    <span className={req.no_humiliation_policy ? 'text-emerald-400' : 'text-slate-500'}>
                      {req.no_humiliation_policy ? '✓' : '✗'} Sin humillaciones
                    </span>
                  </div>
                  
                  {req.notes && (
                    <p className="text-sm text-slate-400 mb-3 bg-slate-900/50 p-2 rounded">{req.notes}</p>
                  )}
                  
                  {req.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={function() { handleAction(req.verification_id, 'scheduled'); }} className="bg-blue-500 hover:bg-blue-600">
                        <Clock className="w-4 h-4 mr-1" />Programar
                      </Button>
                      <Button size="sm" onClick={function() { handleAction(req.verification_id, 'verified'); }} className="bg-emerald-500 hover:bg-emerald-600">
                        <CheckCircle className="w-4 h-4 mr-1" />Verificar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={function() { handleAction(req.verification_id, 'rejected', 'No cumple con los requisitos'); }}>
                        <XCircle className="w-4 h-4 mr-1" />Rechazar
                      </Button>
                    </div>
                  )}
                  
                  {req.status === 'scheduled' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={function() { handleAction(req.verification_id, 'verified'); }} className="bg-emerald-500 hover:bg-emerald-600">
                        <CheckCircle className="w-4 h-4 mr-1" />Aprobar Verificación
                      </Button>
                      <Button size="sm" variant="destructive" onClick={function() { handleAction(req.verification_id, 'rejected', 'No pasó la verificación'); }}>
                        <XCircle className="w-4 h-4 mr-1" />Rechazar
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==================== FEATURED CENTERS ====================

function AdminFeaturedCenters() {
  var [centers, setCenters] = useState([]);
  var [loading, setLoading] = useState(true);
  var [searchTerm, setSearchTerm] = useState('');
  var [updating, setUpdating] = useState(null);

  useEffect(function() {
    loadCenters();
  }, []);

  async function loadCenters() {
    try {
      var res = await authAxios.get(API + '/admin/centers?limit=100', { withCredentials: true });
      setCenters(res.data.centers || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando centros');
    }
    setLoading(false);
  }

  async function toggleFeatured(centerId, currentStatus) {
    setUpdating(centerId);
    try {
      await authAxios.put(API + '/admin/centers/' + centerId + '/feature', { is_featured: !currentStatus }, { withCredentials: true });
      setCenters(function(prev) {
        return prev.map(function(c) {
          if (c.center_id === centerId) {
            return Object.assign({}, c, { is_featured: !currentStatus });
          }
          return c;
        });
      });
      toast.success(currentStatus ? 'Centro quitado de destacados' : 'Centro marcado como destacado');
    } catch (e) {
      console.error(e);
      toast.error('Error actualizando estado');
    }
    setUpdating(null);
  }

  var filteredCenters = centers.filter(function(c) {
    if (!searchTerm) return true;
    return c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.city.toLowerCase().includes(searchTerm.toLowerCase());
  });

  var featuredCenters = filteredCenters.filter(function(c) { return c.is_featured; });
  var normalCenters = filteredCenters.filter(function(c) { return !c.is_featured; });

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border-amber-500/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-50">Centros Destacados</h3>
              <p className="text-sm text-slate-400">Los centros destacados se muestran en la sección "Centros Destacados" de los artículos del blog</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-amber-400">{featuredCenters.length}</p>
              <p className="text-sm text-slate-400">destacados actualmente</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Input
            placeholder="Buscar centro por nombre o ciudad..."
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
            className="bg-slate-950 border-slate-700 text-slate-200 placeholder:text-slate-500"
          />
        </div>
      </div>

      {featuredCenters.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-amber-400 mb-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Centros Destacados ({featuredCenters.length})
          </h4>
          <div className="grid gap-3">
            {featuredCenters.map(function(center) {
              return (
                <Card key={center.center_id} className="bg-gradient-to-r from-amber-500/5 to-amber-600/5 border-amber-500/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {center.logo ? (
                        <img src={center.logo} alt={center.name} className="w-12 h-12 rounded-lg object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-slate-600" />
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-50 flex items-center gap-2">
                          {center.name}
                          {center.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-400" />}
                        </p>
                        <p className="text-sm text-slate-400">{center.city}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={function() { toggleFeatured(center.center_id, true); }}
                        disabled={updating === center.center_id}
                        className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                      >
                        {updating === center.center_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                        <span className="ml-1 hidden sm:inline">Quitar</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <h4 className="text-sm font-medium text-slate-400 mb-3">
          Todos los Centros ({normalCenters.length})
        </h4>
        <div className="grid gap-2 max-h-[400px] overflow-y-auto pr-2">
          {normalCenters.map(function(center) {
            return (
              <Card key={center.center_id} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors">
                <CardContent className="p-3">
                  <div className="flex items-center gap-3">
                    {center.logo ? (
                      <img src={center.logo} alt={center.name} className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-slate-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-50 truncate flex items-center gap-2">
                        {center.name}
                        {center.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                      </p>
                      <p className="text-xs text-slate-400">{center.city}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={function() { toggleFeatured(center.center_id, false); }}
                      disabled={updating === center.center_id}
                      className="bg-amber-500 hover:bg-amber-600"
                    >
                      {updating === center.center_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      <span className="ml-1 hidden sm:inline">Destacar</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ==================== CHAT LEADS (TerapeutAI) ====================

function AdminChatLeads() {
  var [leads, setLeads] = useState([]);
  var [loading, setLoading] = useState(true);
  var [total, setTotal] = useState(0);

  useEffect(function() {
    loadLeads();
  }, []);

  async function loadLeads() {
    try {
      var res = await authAxios.get(API + '/admin/chat-leads', { withCredentials: true });
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando leads');
    }
    setLoading(false);
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    var d = new Date(dateStr);
    return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 border-purple-500/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
              <Mail className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-50">Leads de TerapeutAI</h3>
              <p className="text-sm text-slate-400">Usuarios que completaron el test rápido y dejaron su email para contacto</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-2xl font-bold text-purple-400">{total}</p>
              <p className="text-sm text-slate-400">leads totales</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {leads.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-12 text-center">
            <Mail className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-50 mb-2">Sin leads aún</h3>
            <p className="text-slate-400">Los leads aparecerán aquí cuando los usuarios completen el test de TerapeutAI</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {leads.map(function(lead, idx) {
            return (
              <Card key={lead.lead_id || idx} className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-slate-50">{lead.email}</p>
                        <span className="text-xs text-slate-500">{formatDate(lead.created_at)}</span>
                      </div>
                      {lead.test_results && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                          {lead.test_results.tiempo_consumo && (
                            <div className="bg-slate-950 rounded p-2">
                              <p className="text-xs text-slate-500">Tiempo consumo</p>
                              <p className="text-sm text-slate-200">{lead.test_results.tiempo_consumo}</p>
                            </div>
                          )}
                          {lead.test_results.frecuencia && (
                            <div className="bg-slate-950 rounded p-2">
                              <p className="text-xs text-slate-500">Frecuencia</p>
                              <p className="text-sm text-slate-200">{lead.test_results.frecuencia}</p>
                            </div>
                          )}
                          {lead.test_results.impacto_vida && (
                            <div className="bg-slate-950 rounded p-2">
                              <p className="text-xs text-slate-500">Impacto en vida</p>
                              <p className="text-sm text-slate-200">{lead.test_results.impacto_vida}</p>
                            </div>
                          )}
                          {lead.test_results.region && (
                            <div className="bg-slate-950 rounded p-2">
                              <p className="text-xs text-slate-500">Región</p>
                              <p className="text-sm text-slate-200">{lead.test_results.region}</p>
                            </div>
                          )}
                        </div>
                      )}
                      {lead.recommendation && (
                        <div className="bg-emerald-500/10 rounded-lg p-3 border border-emerald-500/20">
                          <p className="text-xs text-emerald-400 mb-1">Recomendación IA</p>
                          <p className="text-sm text-slate-300 line-clamp-3">{lead.recommendation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== CENTER CONTACTS ====================

// ==================== CONTACTS BY CENTER (admin review) ====================

// Sales kit block helper: top-N items by count, "—" si está vacío
function SalesKitBlock({ title, items, testId }) {
  return (
    <div className="p-3 rounded-lg bg-slate-900/60 border border-slate-800" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{title}</p>
      {(!items || items.length === 0) ? (
        <p className="text-xs text-slate-600">—</p>
      ) : (
        <ul className="space-y-1">
          {items.map(function(it) {
            return (
              <li key={it.key} className="flex items-center justify-between text-xs">
                <span className="text-slate-300 truncate mr-2">{it.key}</span>
                <span className="text-amber-400 font-semibold tabular-nums">{it.count}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function AdminCenterContacts() {
  var [centers, setCenters] = useState([]);
  var [loading, setLoading] = useState(true);
  var [selectedCenter, setSelectedCenter] = useState(null);
  var [centerDetail, setCenterDetail] = useState(null);
  var [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(function() {
    loadCenters();
  }, []);

  async function loadCenters() {
    try {
      var res = await authAxios.get(API + '/admin/centers-contacts', { withCredentials: true });
      setCenters(res.data.centers || []);
    } catch (e) {
      console.error(e);
      toast.error('Error cargando centros');
    }
    setLoading(false);
  }

  async function loadCenterDetail(centerId) {
    setLoadingDetail(true);
    try {
      var res = await authAxios.get(API + '/admin/center/' + centerId + '/contacts', { withCredentials: true });
      setCenterDetail(res.data);
    } catch (e) {
      toast.error('Error cargando detalles');
    }
    setLoadingDetail(false);
  }

  async function markAdmission(messageId, fee) {
    try {
      await authAxios.put(API + '/admin/contact/' + messageId + '/mark-admission', { monthly_fee: fee }, { withCredentials: true });
      toast.success('Ingreso registrado');
      if (selectedCenter) loadCenterDetail(selectedCenter);
      loadCenters();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;
  }

  if (selectedCenter && centerDetail) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={function() { setSelectedCenter(null); setCenterDetail(null); }} className="text-slate-400">
          ← Volver a la lista
        </Button>
        
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-slate-50 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-400" />
              {centerDetail.center?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="center-stats-grid">
              <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-400">{centerDetail.stats?.total_contacts || 0}</p>
                <p className="text-xs text-slate-400">Contactos</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg text-center" data-testid="stat-quotes">
                <p className="text-2xl font-bold text-amber-400">{centerDetail.stats?.total_quotes || 0}</p>
                <p className="text-xs text-slate-400">Cotizaciones</p>
                {centerDetail.stats?.quotes_30d > 0 && <p className="text-[10px] text-amber-300 mt-0.5">+{centerDetail.stats.quotes_30d} en 30 días</p>}
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-400">{centerDetail.stats?.total_whatsapp_clicks || 0}</p>
                <p className="text-xs text-slate-400">WSP clicks</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-emerald-400">{centerDetail.stats?.total_admissions || 0}</p>
                <p className="text-xs text-slate-400">Ingresados</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-amber-400">${(centerDetail.stats?.pending_commission || 0).toLocaleString('es-CL')}</p>
                <p className="text-xs text-slate-400">Comis. pend.</p>
              </div>
              <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                <p className="text-2xl font-bold text-cyan-400">${(centerDetail.stats?.paid_commission || 0).toLocaleString('es-CL')}</p>
                <p className="text-xs text-slate-400">Comis. pagada</p>
              </div>
            </div>

            {/* SALES KIT — datos para vender la suscripción */}
            {centerDetail.sales_kit && centerDetail.sales_kit.quotes_total > 0 && (
              <Card className="bg-gradient-to-br from-amber-500/5 to-emerald-500/5 border-amber-500/20 mb-6" data-testid="sales-kit">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <h4 className="text-sm font-semibold text-amber-300 tracking-wider uppercase">Sales Kit · Para convertir en suscripción</h4>
                  </div>
                  <p className="text-sm text-slate-300 mb-4 leading-relaxed">
                    Este centro recibió <b className="text-amber-400">{centerDetail.sales_kit.quotes_total} cotizaciones</b>
                    {centerDetail.sales_kit.quotes_30d > 0 && <> · <b className="text-amber-400">{centerDetail.sales_kit.quotes_30d}</b> en los últimos 30 días</>}
                    {centerDetail.sales_kit.quotes_90d > 0 && <> · <b>{centerDetail.sales_kit.quotes_90d}</b> en 90 días</>}.
                    Argumentos concretos para la venta:
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <SalesKitBlock title="Sustancias buscadas" items={centerDetail.sales_kit.addictions_top} testId="sk-addictions" />
                    <SalesKitBlock title="Regiones origen" items={centerDetail.sales_kit.regions_top} testId="sk-regions" />
                    <SalesKitBlock title="Presupuestos" items={centerDetail.sales_kit.budgets_top} testId="sk-budgets" />
                    <SalesKitBlock title="Urgencia" items={centerDetail.sales_kit.urgency_top} testId="sk-urgency" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* COTIZACIONES — detalle */}
            {centerDetail.quotes && centerDetail.quotes.length > 0 && (
              <div className="mb-6" data-testid="quotes-section">
                <h4 className="font-medium text-slate-300 mb-3 flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-400" />
                  Cotizaciones recibidas ({centerDetail.quotes.length})
                </h4>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {centerDetail.quotes.map(function(q) {
                    return (
                      <div key={q.quote_id} className="p-3 rounded-lg border bg-amber-500/5 border-amber-500/20" data-testid={`quote-${q.quote_id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-200 truncate">{q.name}</p>
                            <p className="text-sm text-slate-400">{q.email} {q.phone && <>· {q.phone}</>}</p>
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {q.addiction && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/30">{q.addiction}</span>}
                              {q.region && <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-300 border border-blue-500/30">{q.region}</span>}
                              {q.modality && <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600">{q.modality}</span>}
                              {q.budget_range && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">{q.budget_range}</span>}
                              {q.urgency && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">⏱ {q.urgency}</span>}
                            </div>
                            {q.message && <p className="text-xs text-slate-500 mt-2 italic line-clamp-2">"{q.message}"</p>}
                            <p className="text-xs text-slate-600 mt-1.5">{new Date(q.created_at).toLocaleDateString('es-CL')} · matched {q.matched_count || 0} centros</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <h4 className="font-medium text-slate-300 mb-3">Contactos recibidos</h4>
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {centerDetail.contacts?.map(function(c) {
                var isAdmitted = c.admission_status === 'admitted';
                return (
                  <div key={c.message_id} className={'p-3 rounded-lg border ' + (isAdmitted ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700')}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-slate-200">{c.name}</p>
                        <p className="text-sm text-slate-400">{c.email} • {c.phone}</p>
                        {c.message && <p className="text-sm text-slate-500 mt-1">{c.message}</p>}
                        <p className="text-xs text-slate-600 mt-1">{new Date(c.created_at).toLocaleDateString('es-CL')}</p>
                        {isAdmitted && <p className="text-emerald-400 text-sm mt-1">✓ Ingresado - Comisión: ${(c.commission_amount || 0).toLocaleString('es-CL')}</p>}
                      </div>
                      {!isAdmitted && (
                        <Button size="sm" onClick={function() {
                          var fee = prompt('Mensualidad del paciente (CLP):');
                          if (fee) markAdmission(c.message_id, parseInt(fee));
                        }} className="bg-emerald-500 hover:bg-emerald-600">
                          Marcar Ingreso
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-blue-500/10 to-blue-600/10 border-blue-500/30">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-50">Contactos por Centro</h3>
              <p className="text-sm text-slate-400">Visualiza y valida los contactos que han consultado a cada centro</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {centers.map(function(center) {
          return (
            <Card key={center.center_id} className="bg-slate-900 border-slate-800 hover:border-slate-700 cursor-pointer transition-colors" onClick={function() { setSelectedCenter(center.center_id); loadCenterDetail(center.center_id); }}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-8 h-8 text-slate-600" />
                    <div>
                      <p className="font-medium text-slate-50 flex items-center gap-2">
                        {center.name}
                        {center.is_premium && <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded">Premium</span>}
                        {center.is_verified && <BadgeCheck className="w-4 h-4 text-emerald-400" />}
                      </p>
                      <p className="text-sm text-slate-400">{center.city}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-bold text-blue-400" data-testid={`contacts-count-${center.center_id}`}>{center.total_contacts || 0}</p>
                      <p className="text-xs text-slate-500">Contactos</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-amber-400" data-testid={`quotes-count-${center.center_id}`}>{center.total_quotes || 0}</p>
                      <p className="text-xs text-slate-500">Cotizac.</p>
                      {center.quotes_last_30d > 0 && (
                        <p className="text-[10px] text-amber-300 mt-0.5">+{center.quotes_last_30d} 30d</p>
                      )}
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-green-400">{center.total_whatsapp_clicks || 0}</p>
                      <p className="text-xs text-slate-500">WSP</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-emerald-400">{center.total_admissions || 0}</p>
                      <p className="text-xs text-slate-500">Ingresados</p>
                    </div>
                    <Eye className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ==================== COMMISSIONS ====================

function AdminCommissions() {
  var [commissions, setCommissions] = useState([]);
  var [loading, setLoading] = useState(true);
  var [filter, setFilter] = useState('pending');
  var [totals, setTotals] = useState({ pending: 0, paid: 0 });

  useEffect(function() {
    loadCommissions();
  }, [filter]);

  async function loadCommissions() {
    setLoading(true);
    try {
      var url = API + '/admin/referral-commissions';
      if (filter !== 'all') url += '?status=' + filter;
      var res = await authAxios.get(url, { withCredentials: true });
      setCommissions(res.data.commissions || []);
      setTotals({ pending: res.data.total_pending || 0, paid: res.data.total_paid || 0 });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  async function markPaid(commissionId) {
    try {
      await authAxios.put(API + '/admin/referral-commissions/' + commissionId + '/pay', {}, { withCredentials: true });
      toast.success('Comisión marcada como pagada');
      loadCommissions();
    } catch (e) {
      toast.error('Error al procesar');
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border-amber-500/30">
          <CardContent className="p-6">
            <p className="text-3xl font-bold text-amber-400">${totals.pending.toLocaleString('es-CL')}</p>
            <p className="text-sm text-slate-400">Comisiones Pendientes</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border-emerald-500/30">
          <CardContent className="p-6">
            <p className="text-3xl font-bold text-emerald-400">${totals.paid.toLocaleString('es-CL')}</p>
            <p className="text-sm text-slate-400">Comisiones Pagadas</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2">
        <Button variant={filter === 'pending' ? 'default' : 'outline'} onClick={function() { setFilter('pending'); }} className={filter === 'pending' ? 'bg-amber-500' : 'border-slate-700'}>Pendientes</Button>
        <Button variant={filter === 'paid' ? 'default' : 'outline'} onClick={function() { setFilter('paid'); }} className={filter === 'paid' ? 'bg-emerald-500' : 'border-slate-700'}>Pagadas</Button>
        <Button variant={filter === 'all' ? 'default' : 'outline'} onClick={function() { setFilter('all'); }} className={filter === 'all' ? 'bg-slate-700' : 'border-slate-700'}>Todas</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>
      ) : commissions.length === 0 ? (
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-400">No hay comisiones {filter !== 'all' ? filter === 'pending' ? 'pendientes' : 'pagadas' : ''}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {commissions.map(function(c) {
            return (
              <Card key={c.commission_id} className={'bg-slate-900 border-slate-800 ' + (c.status === 'paid' ? 'opacity-70' : '')}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-50">{c.center_name}</p>
                      <p className="text-sm text-slate-400">Paciente: {c.patient_name} • {c.patient_email}</p>
                      <p className="text-xs text-slate-500">Mensualidad: ${(c.monthly_fee || 0).toLocaleString('es-CL')} • {new Date(c.created_at).toLocaleDateString('es-CL')}</p>
                    </div>
                    <div className="text-right flex items-center gap-4">
                      <div>
                        <p className="text-xl font-bold text-amber-400">${(c.commission_amount || 0).toLocaleString('es-CL')}</p>
                        <p className="text-xs text-slate-500">{c.status === 'paid' ? '✓ Pagado' : 'Pendiente'}</p>
                      </div>
                      {c.status === 'pending' && (
                        <Button size="sm" onClick={function() { markPaid(c.commission_id); }} className="bg-emerald-500 hover:bg-emerald-600">
                          <CheckCircle className="w-4 h-4 mr-1" />Pagar
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminContent() {
  var [content, setContent] = useState(null);
  var [loading, setLoading] = useState(true);
  var [saving, setSaving] = useState(false);
  var [formData, setFormData] = useState({});
  var [videoData, setVideoData] = useState({
    centers_video: 'https://youtu.be/K_BGQD0veHI?si=B81zSGw8-LgnxScL',
    professionals_video: 'https://youtu.be/j8-UigqG6SE?si=NVvcDfiLVrDYjt3b',
    workshops_video: 'https://youtu.be/RZA1cV74mGs?si=dtoyxUZsElyqY55B'
  });
  var [savingVideos, setSavingVideos] = useState(false);
  var [videoResults, setVideoResults] = useState(null);

  useEffect(function() {
    async function load() {
      try {
        var res = await authAxios.get(API + '/site-content');
        setContent(res.data);
        setFormData(res.data);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      await authAxios.put(API + '/admin/site-content', formData, { withCredentials: true });
      toast.success('Contenido actualizado');
    } catch (e) {
      toast.error('Error');
    }
    setSaving(false);
  }

  async function handleUpdateVideos() {
    setSavingVideos(true);
    setVideoResults(null);
    try {
      var res = await authAxios.post(API + '/admin/bulk-update-videos', videoData, { withCredentials: true });
      setVideoResults(res.data.results);
      toast.success('Videos actualizados en todos los perfiles');
    } catch (e) {
      console.error(e);
      toast.error('Error actualizando videos');
    }
    setSavingVideos(false);
  }

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-400" /></div>;

  return (
    <div className="space-y-6">
      {/* YouTube Videos Section */}
      <Card className="bg-slate-900 border-0 shadow-sm border-l-4 border-l-red-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="text-red-500">▶</span> Videos de YouTube (Masivo)
          </CardTitle>
          <p className="text-sm text-slate-400">Actualiza el video de YouTube de todos los perfiles a la vez</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Video para Centros de Rehabilitación</Label>
            <Input 
              value={videoData.centers_video} 
              onChange={function(e) { setVideoData(function(prev) { return Object.assign({}, prev, { centers_video: e.target.value }); }); }}
              placeholder="https://youtu.be/..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label>Video para Profesionales</Label>
            <Input 
              value={videoData.professionals_video} 
              onChange={function(e) { setVideoData(function(prev) { return Object.assign({}, prev, { professionals_video: e.target.value }); }); }}
              placeholder="https://youtu.be/..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
          <div>
            <Label className="text-slate-400">Video para Talleres <span className="text-amber-400 text-xs ml-2">⚠ Deshabilitado</span></Label>
            <Input
              value={videoData.workshops_video}
              disabled
              readOnly
              placeholder="Asigna el video por workshop individual (causaba 260 Soft 404)"
              className="bg-slate-800 border-slate-700 opacity-50 cursor-not-allowed"
            />
            <p className="text-xs text-amber-400/80 mt-1.5">Asignar el mismo video a 352 talleres generó errores SEO. Edita cada taller por separado.</p>
          </div>
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-3 flex items-start gap-2">
            <div className="text-amber-400">⚠</div>
            <div className="text-xs text-amber-100/85 leading-relaxed">
              <p className="mb-1.5"><b>Limpieza de videos duplicados en workshops:</b> si veo videos repetidos asignados masivamente, dale al botón para removerlos. Esto soluciona los Soft 404 de Video en Google Search Console.</p>
              <Button
                onClick={async function() {
                  try {
                    var res = await authAxios.post(API + '/admin/workshops/clear-duplicate-videos', {}, { withCredentials: true });
                    toast.success((res.data && res.data.message) || 'Limpieza completada');
                  } catch (e) {
                    toast.error('Error en la limpieza');
                  }
                }}
                size="sm"
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 mt-1"
                data-testid="clear-duplicate-videos-btn"
              >
                Limpiar videos duplicados en workshops
              </Button>
            </div>
          </div>
          {videoResults && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <p className="text-emerald-400 font-medium mb-2">✅ Videos actualizados:</p>
              <ul className="text-sm text-slate-300 space-y-1">
                {videoResults.centers_updated !== undefined && <li>• Centros: {videoResults.centers_updated} actualizados</li>}
                {videoResults.professionals_updated !== undefined && <li>• Profesionales: {videoResults.professionals_updated} actualizados</li>}
                {videoResults.workshops_updated !== undefined && <li>• Talleres: {videoResults.workshops_updated} actualizados</li>}
              </ul>
            </div>
          )}
          <Button 
            onClick={handleUpdateVideos} 
            className="w-full rounded-full py-4 bg-red-600 hover:bg-red-700" 
            disabled={savingVideos}
          >
            {savingVideos ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Actualizando...</> : <><span className="mr-2">▶</span>Actualizar Videos en Todos los Perfiles</>}
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-slate-900 border-0 shadow-sm">
        <CardHeader><CardTitle>Sección Hero</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div><Label>Título principal</Label><Input value={formData.hero_title || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { hero_title: e.target.value }); }); }} /></div>
          <div><Label>Subtítulo</Label><Textarea value={formData.hero_subtitle || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { hero_subtitle: e.target.value }); }); }} rows={2} /></div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900 border-0 shadow-sm">
        <CardHeader><CardTitle>Contacto</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Email</Label><Input value={formData.contact_email || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { contact_email: e.target.value }); }); }} /></div>
            <div><Label>Teléfono</Label><Input value={formData.contact_phone || ''} onChange={function(e) { setFormData(function(prev) { return Object.assign({}, prev, { contact_phone: e.target.value }); }); }} /></div>
          </div>
        </CardContent>
      </Card>
      <Button onClick={handleSave} className="w-full rounded-full py-6 bg-emerald-500 hover:bg-emerald-600" disabled={saving}>
        {saving ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Guardando...</> : <><Save className="w-5 h-5 mr-2" />Guardar contenido</>}
      </Button>
    </div>
  );
}


// Workshop Type Labels
var workshopTypeLabels = {
  teatro: 'Teatro',
  meditacion: 'Meditación',
  baile: 'Baile',
  yoga: 'Yoga',
  arte: 'Arte',
  musica: 'Música',
  deportes: 'Deportes',
  mindfulness: 'Mindfulness',
  terapia_grupal: 'Terapia Grupal',
  escritura: 'Escritura',
  otro: 'Otro'
};
